import { FC, useMemo } from "react";

import {
  FilterByIdListConfigurationView,
  FilterByIdListItem,
} from "@/domain/view/components/datatable/datatable-filters/filter-by-id-list-configuration-view.tsx";

import { useSmartUserList } from "../../hooks/api/use-smart-user-list";
import { UserViewOptional } from "../user-view-optional";

export interface DatatableAssigneeConfigurationViewProps {
  workspaceId: string;
  columnId: string;
  onFinished: () => void;
}

/**
 * Configuration view for assignee column filters in datatables.
 *
 * **Security**: Uses `useSmartUserList` to fetch user data safely in both
 * public and private contexts:
 * - Public dashboards: Shows sanitized user data (no email/username)
 * - Private dashboards: Shows full user data
 */
export const DatatableAssigneeConfigurationView: FC<DatatableAssigneeConfigurationViewProps> = ({
  onFinished,
  columnId,
}) => {
  // SECURITY: Use smart hook that switches between public/private data sources
  const { filterUsers } = useSmartUserList();

  const activeUserList = filterUsers({
    workspaceMembership: {
      status: ["ACTIVE"],
    },
  });

  const itemList = useMemo(() => {
    return activeUserList.map((user) => ({
      id: user.id,
      name: user.username ?? user.email,
    }));
  }, [activeUserList]);

  const renderItem = (item: FilterByIdListItem | null, view: "icon" | "text" | "icon-text") => {
    const showText = view === "text" || view === "icon-text";

    const user = activeUserList.find((user) => user.id === item?.id);

    return <UserViewOptional key={item?.id} user={user ?? null} logoOnly={!showText} />;
  };

  return (
    <FilterByIdListConfigurationView
      columnId={columnId}
      itemList={itemList}
      renderItem={renderItem}
      onFinished={onFinished}
    />
  );
};
